import {
  Component,
  OnInit,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Output,
  Input,
  EventEmitter,
  SimpleChanges
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import Popper from 'popper.js';

interface Criterion {
  key: string;
  messageKey: string;
  validator: (pwd: string) => boolean;
  passed: boolean;
}

@Component({
  selector: 'app-password-validator',
  templateUrl: './password-validator.component.html',
  styleUrls: ['./password-validator.component.scss']
})
export class PasswordValidatorComponent implements OnInit, AfterViewInit {
  @Input() isRowStyle = false;
  @Input() isReset: boolean = false;

  @Output() validChange = new EventEmitter<boolean>();
  @Output() passwordChange = new EventEmitter<string>();

  @ViewChild('passwordInput', { static: true }) passwordInput!: ElementRef<HTMLInputElement>;
  @ViewChild('popover', { static: true }) popover!: ElementRef<HTMLElement>;

  form!: FormGroup;
  showPassword = false;
  popperInstance!: Popper;
  popoverVisible = false;

  criteriaList: Criterion[] = [
    { key: 'minLength',  messageKey: 'PASSWORD_CRITERIA.MIN_LENGTH', validator: pwd => pwd.length >= 8,    passed: false },
    { key: 'upperCase',  messageKey: 'PASSWORD_CRITERIA.UPPERCASE',   validator: pwd => /[A-Z]/.test(pwd),      passed: false },
    { key: 'number',     messageKey: 'PASSWORD_CRITERIA.NUMBER',      validator: pwd => /[0-9]/.test(pwd),      passed: false },
  ];
  allPassed = false;
  match = false;

  constructor(private fb: FormBuilder) {}

  ngOnInit() {
    this.form = this.fb.group({
      password: ['', Validators.required],
      confirmPassword: ['', Validators.required]
    });

    this.form.valueChanges.subscribe(() => {
      this.validate();
      if (this.popoverVisible) {
        this.popperInstance.scheduleUpdate();
      }
    });
  }

  formReset(): void {
    this.form.reset();
    this.allPassed = false;
    this.match = false;
    this.popoverVisible = false;
  }

  ngAfterViewInit() {
    this.popperInstance = new Popper(
      this.passwordInput.nativeElement,
      this.popover.nativeElement,
      {
        placement: 'top',
        modifiers: {
          
          preventOverflow: { boundariesElement: 'viewport' }
        }
      }
    );
  }

  togglePopover(show: boolean) {
    this.popoverVisible = show;
    if (show) {
      this.popperInstance.scheduleUpdate();
    }
  }

  private validate() {
    const pwd     = this.form.get('password')!.value   || '';
    const confirm = this.form.get('confirmPassword')!.value || '';

    this.criteriaList.forEach(c => c.passed = c.validator(pwd));
    this.allPassed = this.criteriaList.every(c => c.passed);
    this.match     = pwd !== '' && pwd === confirm;

    this.form.get('password')!
      .setErrors(this.allPassed ? null : { invalid: true });
    this.form.get('confirmPassword')!
      .setErrors(this.match ? null : { mismatch: true });

    const valid = this.allPassed && this.match;
    this.validChange.emit(valid);
    if (valid) {
      this.passwordChange.emit(pwd);
    }
    this.isReset = false;
  }

  onPaste(event: ClipboardEvent) {
    const text = event.clipboardData?.getData('text/plain') || '';
    if (/\s/.test(text)) {
      event.preventDefault();
    }
  }

  toggleShow() {
    this.showPassword = !this.showPassword;
  }

  generateRandom() {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';
    let pwd = this.randomChar(upper) + this.randomChar(digits);
    const all = upper + lower + digits;
    while (pwd.length < 8) {
      pwd += this.randomChar(all);
    }
    pwd = pwd.split('').sort(() => Math.random() - 0.5).join('');
    this.form.patchValue({ password: pwd, confirmPassword: pwd });
    this.showPassword = true;
  }

  private randomChar(set: string) {
    return set.charAt(Math.floor(Math.random() * set.length));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isReset'] && changes['isReset'].currentValue === true) {
      this.formReset(); 
    }
  }
}
